import JSZip from 'jszip';

// ----------------------------------------------------------------------
// The printed QR card, 5 × 7 in (canvas board "Table QR Card · Red band").
// Ported from admin-app (src/components/restaurant-detail/qrCard.ts) so the
// restaurant admin prints the very same card the super admin console does.
// One drawing serves the preview, the PNG download and the print sheet, so
// what staff see is what prints. Drawn on a 480 × 672 grid and scaled to
// 1500 × 2100 px, i.e. 300 dpi at 5 × 7 in.
//
// Framework-free on purpose: nothing here touches Angular.
// ----------------------------------------------------------------------

/** Which QR a card carries; the grey code at its foot tells printed cards apart. */
export type QrCardKind = 'dining' | 'dine' | 'togo';

export interface QrCardSpec {
  kind: QrCardKind;
  /** "12" for a To-Go dining table, the table's name for dine-in, ignored for To-Go. */
  label: string;
  restaurantName: string;
  /** Restaurant logo; initials on a coloured square when missing or unreadable. */
  logoUrl?: string | null;
  /** The branded QR PNG. */
  qrUrl: string;
  /** To-Go only: the counter code also takes eat-in orders. */
  togoDineIn?: boolean;
}

export const CARD_WIDTH_IN = 5;
export const CARD_HEIGHT_IN = 7;
export const CARD_PX = { width: 1500, height: 2100 };
const GRID = { width: 480, height: 672 };

// Print colours: the card is a fixed brand artefact, so these deliberately do
// not follow the dashboard theme.
const ACCENT = '#E15541';
const INK = '#121A2A';
const MUTED = '#4A5160';
const FOOT = '#3A4150';
const CODE = '#8A909C';
const FONT = "'Lexend', 'Helvetica Neue', Arial, sans-serif";
const MONO = "ui-monospace, Menlo, 'SF Mono', monospace";

// The Fynkle mark, in its own 51 × 63 box starting at (3, -1).
const MARK =
  'M45.1143 44.7107L30.2416 60.0285C28.9839 61.3238 26.9569 61.3238 25.6992 60.0285L10.8265 44.7107C7.05432 40.818 4.78497 35.9617 4.0185 30.9097C3.87583 29.9693 4.57658 29.1413 5.49148 29.0964L12.0502 28.7746C13.2196 28.7158 14.3302 29.2939 14.9588 30.2887L16.398 32.5663C18.3836 35.7087 21.9619 37.4517 25.6409 37.0683L41.7004 35.3951C42.5727 35.3042 43.2356 34.5639 43.2356 33.6807C43.2356 32.7289 42.4694 31.9573 41.5242 31.9573L27.3682 31.9573C26.2447 31.9573 25.3339 31.04 25.3339 29.9086C25.3339 28.7771 26.2447 27.8599 27.3682 27.8599H41.6082C42.507 27.8599 43.2356 27.1261 43.2356 26.221C43.2356 25.3158 42.507 24.5821 41.6082 24.5821H27.3682C26.2447 24.5821 25.3339 23.6648 25.3339 22.5334C25.3339 21.402 26.2447 20.4847 27.3682 20.4847H41.5242C42.4694 20.4847 43.2356 19.7131 43.2356 18.7612C43.2356 17.8781 42.5727 17.1378 41.7003 17.0469L25.6409 15.3737C21.9619 14.9903 18.3836 16.7333 16.398 19.8757L14.9588 22.1533C14.3302 23.1481 13.2196 23.7262 12.0502 23.6673L5.78465 23.36C4.82881 23.3131 4.11765 22.415 4.33137 21.4455C5.31037 17.0047 7.47542 12.7861 10.8265 9.328C20.2948 -0.442666 35.646 -0.442666 45.1143 9.328C54.5826 19.0987 54.5826 34.94 45.1143 44.7107Z';

const asTable = (label: string) =>
  /^\d+$/.test(label.trim()) ? `Table ${label.trim()}` : label.trim();

/** A table name as a file name: anything a file system would choke on becomes a dash. */
export function safeFileName(name: string) {
  const cleaned = name
    .trim()
    .replace(/[\\/:*?"<>|\p{Cc}]+/gu, '-')
    .replace(/\s+/g, ' ');
  return cleaned || 'QR-Code';
}

/** The words on a card, and its staff code ("TD-12", "DI-PATIO1", "TG"). */
export function cardCopy(spec: Pick<QrCardSpec, 'kind' | 'label' | 'togoDineIn'>) {
  const short = spec.label.replace(/\s+/g, '').toUpperCase();
  switch (spec.kind) {
    case 'togo':
      return {
        headline: 'To-Go',
        title: 'Scan to order & pay',
        line: spec.togoDineIn
          ? 'Take it away, or eat here and add your table.'
          : 'Pick it up at the counter when it’s ready.',
        code: 'TG',
        codeName: 'TO-GO COUNTER',
      };
    case 'dine':
      return {
        headline: asTable(spec.label) || 'Table',
        title: 'Scan to order & pay',
        line: 'Point your phone camera at the code.',
        code: `DI-${short}`,
        codeName: 'DINE-IN',
      };
    default:
      return {
        headline: asTable(spec.label),
        title: 'Scan to order & pay',
        line: 'Point your phone camera at the code.',
        code: `TD-${short}`,
        codeName: 'TO-GO DINING',
      };
  }
}

/** "Bella-Roma-TD-12" — what a downloaded card is called. */
export function cardFileName(spec: QrCardSpec) {
  return safeFileName(`${spec.restaurantName} ${cardCopy(spec).code}`).replace(/\s+/g, '-');
}

export function initials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || 'R';
}

/**
 * Fetched as a blob first: the storage host serves CORS, so the canvas stays
 * exportable. `cache: 'reload'` because the same logo is also shown by a plain
 * <img> (sidebar, profile); S3 answers that request without CORS headers and
 * without `Vary: Origin`, and Chrome would hand that cached copy to this CORS
 * fetch, which then fails and the card falls back to initials.
 */
async function loadImage(url: string | null | undefined): Promise<ImageBitmap | null> {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'reload' });
    if (!res.ok) return null;
    return await createImageBitmap(await res.blob());
  } catch {
    return null;
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number | [number, number, number, number],
) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

function drawMark(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(w / 51, h / 63);
  ctx.translate(-3, 1);
  ctx.fillStyle = ACCENT;
  ctx.fill(new Path2D(MARK));
  ctx.restore();
}

/** Shrinks the font until ``text`` fits ``maxWidth``. */
function fitFont(
  ctx: CanvasRenderingContext2D,
  text: string,
  weight: number,
  size: number,
  maxWidth: number,
  family = FONT,
) {
  let px = size;
  ctx.font = `${weight} ${px}px ${family}`;
  while (px > 10 && ctx.measureText(text).width > maxWidth) {
    px -= 1;
    ctx.font = `${weight} ${px}px ${family}`;
  }
}

/** Draws the card and returns it as a 1500 × 2100 PNG. */
export async function renderQrCard(spec: QrCardSpec): Promise<Blob> {
  await document.fonts?.load(`700 52px ${FONT}`).catch(() => undefined);
  const [logo, qr] = await Promise.all([loadImage(spec.logoUrl), loadImage(spec.qrUrl)]);

  const canvas = document.createElement('canvas');
  canvas.width = CARD_PX.width;
  canvas.height = CARD_PX.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot draw the QR card.');
  ctx.scale(CARD_PX.width / GRID.width, CARD_PX.height / GRID.height);
  const copy = cardCopy(spec);
  const mid = GRID.width / 2;

  // Ground and the red band, rounded at its foot.
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, GRID.width, GRID.height);
  ctx.fillStyle = ACCENT;
  roundRect(ctx, 0, 0, GRID.width, 300, [0, 0, 40, 40]);
  ctx.fill();

  // Logo on a white pad (or initials).
  const logoH = 64;
  const logoW = logo ? Math.min(220, Math.round((logo.width / logo.height) * logoH)) : 64;
  const padX = mid - logoW / 2 - 4;
  ctx.fillStyle = '#FFFFFF';
  roundRect(ctx, padX, 40, logoW + 8, logoH + 8, 14);
  ctx.fill();
  ctx.save();
  roundRect(ctx, padX + 4, 44, logoW, logoH, 10);
  ctx.clip();
  if (logo) {
    ctx.drawImage(logo, padX + 4, 44, logoW, logoH);
  } else {
    ctx.fillStyle = INK;
    ctx.fillRect(padX + 4, 44, logoW, logoH);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = `700 24px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials(spec.restaurantName), mid, 44 + logoH / 2 + 1);
  }
  ctx.restore();

  // Restaurant name and the big headline.
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  fitFont(ctx, spec.restaurantName, 700, 22, GRID.width - 80);
  ctx.fillText(spec.restaurantName, mid, 140);
  fitFont(ctx, copy.headline, 700, 52, GRID.width - 80);
  ctx.fillText(copy.headline, mid, 197);

  // The QR on a white tile, overlapping the band, with the Fynkle mark at its heart.
  ctx.save();
  ctx.shadowColor = 'rgba(18, 26, 42, 0.16)';
  ctx.shadowBlur = 30;
  ctx.shadowOffsetY = 10;
  ctx.fillStyle = '#FFFFFF';
  roundRect(ctx, 106, 236, 268, 268, 24);
  ctx.fill();
  ctx.restore();
  if (qr) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qr, 126, 256, 228, 228);
    ctx.imageSmoothingEnabled = true;
  }
  ctx.fillStyle = '#FFFFFF';
  roundRect(ctx, mid - 26, 370 - 26, 52, 52, 13);
  ctx.fill();
  drawMark(ctx, mid - 15, 370 - 18, 30, 36);

  // Call to action.
  ctx.fillStyle = INK;
  fitFont(ctx, copy.title, 700, 26, GRID.width - 80);
  ctx.fillText(copy.title, mid, 542);
  ctx.fillStyle = MUTED;
  fitFont(ctx, copy.line, 400, 14, GRID.width - 80);
  ctx.fillText(copy.line, mid, 574);

  // "Ordering by Fynkle".
  ctx.font = `600 13px ${FONT}`;
  const foot = 'Ordering by Fynkle';
  const footW = ctx.measureText(foot).width + 16 + 8;
  const footX = mid - footW / 2;
  drawMark(ctx, footX, 616, 16, 20);
  ctx.fillStyle = FOOT;
  ctx.textAlign = 'left';
  ctx.fillText(foot, footX + 24, 626);

  // Staff code, small and grey, along the foot.
  ctx.fillStyle = CODE;
  ctx.font = `500 9px ${MONO}`;
  ctx.letterSpacing = '0.7px';
  ctx.textAlign = 'left';
  ctx.fillText(copy.code, 24, 654);
  ctx.textAlign = 'right';
  ctx.fillText(copy.codeName, GRID.width - 24, 654);

  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The card could not be drawn.'))),
      'image/png',
    ),
  );
}

function saveBlob(blob: Blob, fileName: string) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/** One card: its PNG. Several: one zip of PNGs, named after the restaurant and type. */
export async function downloadCards(specs: QrCardSpec[], zipName: string) {
  if (specs.length === 0) return;
  if (specs.length === 1) {
    saveBlob(await renderQrCard(specs[0]), `${cardFileName(specs[0])}.png`);
    return;
  }
  const zip = new JSZip();
  for (const spec of specs) zip.file(`${cardFileName(spec)}.png`, await renderQrCard(spec));
  saveBlob(
    await zip.generateAsync({ type: 'blob' }),
    `${safeFileName(zipName).replace(/\s+/g, '-')}.zip`,
  );
}

/**
 * Opens the print sheet: one 5 × 7 in page per card. The window is opened
 * first (pop-up blockers allow it only inside the click), then filled.
 * Returns false when the browser blocked it.
 */
export async function printCards(specs: QrCardSpec[], title: string) {
  if (specs.length === 0) return true;
  const w = window.open('', '_blank', 'width=900,height=700');
  if (!w) return false;
  w.document.write(
    '<!doctype html><title>Preparing cards…</title><p style="font-family:system-ui;padding:24px">Preparing cards…</p>',
  );
  const blobs = await Promise.all(specs.map(renderQrCard));
  const urls = blobs.map((b) => URL.createObjectURL(b));
  const esc = (s: string) =>
    s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
  w.document.open();
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
@page{size:${CARD_WIDTH_IN}in ${CARD_HEIGHT_IN}in;margin:0}
html,body{margin:0;padding:0;background:#fff}
img{display:block;width:${CARD_WIDTH_IN}in;height:${CARD_HEIGHT_IN}in;page-break-after:always;break-after:page}
img:last-child{page-break-after:auto;break-after:auto}
@media screen{body{background:#eef0f3;padding:16px}img{margin:0 auto 16px;box-shadow:0 1px 6px rgba(0,0,0,.15)}}
</style></head><body>${urls.map((u, i) => `<img src="${u}" alt="${esc(cardFileName(specs[i]))}">`).join('')}</body></html>`);
  w.document.close();
  const imgs = Array.from(w.document.images);
  await Promise.all(
    imgs.map((img) =>
      img.complete ? Promise.resolve() : new Promise((r) => (img.onload = img.onerror = r)),
    ),
  );
  w.focus();
  w.print();
  return true;
}
